import {
  BuildingType,
  CountryCode,
  EnergyEfficiencyClass,
  getEnergyEfficiencyLabel,
  COUNTRY_CODES,
  ProjectStatus,
} from '@belimo-retrofit-portal/logic';
import { eqStrict, contramap } from 'fp-ts/Eq';
import { pipe } from 'fp-ts/function';
import * as N from 'fp-ts/number';
import * as O from 'fp-ts/Ord';
import * as A from 'fp-ts/ReadonlyArray';
import * as S from 'fp-ts/string';
import { createSelector } from 'reselect';
import { STATUS_OPTIONS } from 'src/modules/basedata/constants/statusOptions';
import { FACTOR_WEIGHTS_LABELS } from 'src/modules/common/constants/factorWeightedLabel';
import { BUILDING_TYPE_OPTIONS } from 'src/modules/common/constants/options';
import { ProjectOwner } from 'src/modules/common/types/ProjectOwner';
import { getLoggedInUser } from 'src/modules/login/selectors/getLoggedInUser';
import { getProjectListData } from 'src/modules/project-list/selectors/getProjectListData';
import { ProjectListFilterValues } from 'src/modules/project-list/types/ProjectListFilterValues';
import { getProjectFilter } from 'src/modules/project-list/utils/filter';
import { isNotNull } from 'src/utils/guard';

export const getProjectListFilterValues = createSelector([
  getProjectListData,
  getLoggedInUser,
], ({ projects, query }, authUser): ProjectListFilterValues => {
  const filtered = projects.filter(getProjectFilter(query.filter));

  const ownerOrd: O.Ord<ProjectOwner> = O.fromCompare((a, b) => {
    if (a.email === b.email) {
      return 0;
    }
    if (a.email === authUser.email) {
      return -1;
    }
    if (b.email === authUser.email) {
      return +1;
    }

    return S.Ord.compare(a.email, b.email);
  });

  return ({
    buildingType: pipe(
      filtered,
      (data) => data.map((it) => it.project.data.building.type),
      A.uniq<BuildingType>(eqStrict),
      A.sort(BUILDING_TYPE_ORD),
    ),

    status: pipe(
      filtered,
      (data) => data.map((it) => it.project.data.status),
      A.uniq<ProjectStatus>(eqStrict),
      A.sort(STATUS_ORD),
    ),

    country: pipe(
      filtered,
      (data) => data.map((it) => it.project.data.building.address.country),
      A.uniq<CountryCode>(eqStrict),
      A.sort(COUNTRY_CODE_ORD),
    ),
    city: pipe(
      filtered,
      (data) => data.map((it) => it.project.data.building.address.city),
      (data) => data.filter((it) => it !== ''),
      A.uniq(S.Eq),
      A.sort(S.Ord),
    ),

    ratingActual: pipe(
      filtered,
      (data) => data.map((it) => it.rating.actual),
      (data) => data.filter(isNotNull),
      (data) => data.map(getEnergyEfficiencyLabel),
      A.uniq<EnergyEfficiencyClass>(eqStrict),
      A.sort(EFFICIENCY_LABEL_ORD),
    ),
    ratingFuture: pipe(
      filtered,
      (data) => data.map((it) => it.rating.future),
      (data) => data.filter(isNotNull),
      (data) => data.map(getEnergyEfficiencyLabel),
      A.uniq<EnergyEfficiencyClass>(eqStrict),
      A.sort(EFFICIENCY_LABEL_ORD),
    ),

    ownership: pipe(
      filtered,
      A.map((it) => it.project.owner),
      A.filter(isNotNull),
      A.uniq(contramap((owner: ProjectOwner) => owner.email)(eqStrict)),
      A.sort(ownerOrd),
    ),
  });
});

const BUILDING_TYPE_ORD = pipe(
  N.Ord,
  O.contramap((value: BuildingType) => BUILDING_TYPE_OPTIONS.indexOf(value)),
);

const STATUS_ORD = pipe(
  N.Ord,
  O.contramap((value: ProjectStatus) => STATUS_OPTIONS.indexOf(value)),
);

const COUNTRY_CODE_ORD = pipe(
  N.Ord,
  O.contramap((value: CountryCode) => COUNTRY_CODES.indexOf(value)),
);

const EFFICIENCY_LABEL_ORD = pipe(
  N.Ord,
  O.contramap((value: EnergyEfficiencyClass) => FACTOR_WEIGHTS_LABELS[value].toNumber()),
  O.reverse,
);
